// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/pages/ReconciliationWorkbenchListPage.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  RefreshCw
} from "lucide-react";

import {
  useNavigate
} from "react-router-dom";

import axiosClient
from "../../../../api/axiosClient";


// =============================================================================
// COMPONENT
// =============================================================================

export default function ReconciliationWorkbenchListPage() {

  // ===========================================================================
  // NAVIGATION
  // ===========================================================================
  const navigate =
    useNavigate();


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [error, setError] =
    useState("");

  const [filters, setFilters] =
    useState({

      status: "",

      search: ""
    });


  // ===========================================================================
  // LOAD DATA
  // ===========================================================================
  const loadReceipts =
    async () => {

      try {

        setLoading(true);

        setError("");

        const response =
          await axiosClient.get(

            "/api/pms/material-receipt/list",

            {
              params: filters
            }
          );

        if (
          response?.data?.success
        ) {

          let data =
            response.data.data || [];


          // ===============================================================
          // ONLY RECONCILIATION STATUSES
          // ===============================================================
          data = data.filter((x) =>

            [
              "SUBMITTED",
              "RECONCILED"
            ].includes(
              x.receipt_status
            )
          );

          setRows(data);

        } else {

          setRows([]);
        }

      } catch (err) {

        console.error(err);

        setError(
          "Failed to load reconciliation records"
        );

      } finally {

        setLoading(false);
      }
    };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    loadReceipts();

  }, []);


  // ===========================================================================
  // FILTER CHANGE
  // ===========================================================================
  const handleFilterChange = (
    field,
    value
  ) => {

    setFilters((prev) => ({

      ...prev,

      [field]: value
    }));
  };


  // ===========================================================================
  // APPLY FILTERS
  // ===========================================================================
  const handleApplyFilters =
    () => {

      loadReceipts();
    };


  // ===========================================================================
  // OPEN RECONCILIATION
  // ===========================================================================
  const openReconciliation = (
    receiptId
  ) => {

    navigate(

      `/ipe/pms/material-reconciliation/${receiptId}`
    );
  };


  // ===========================================================================
  // KPI
  // ===========================================================================
  const kpis = {

    submitted: rows.filter(
      (x) =>
        x.receipt_status ===
        "SUBMITTED"
    ).length,

    reconciled: rows.filter(
      (x) =>
        x.receipt_status ===
        "RECONCILED"
    ).length,

    acceptedQty: rows.reduce(

      (sum, x) =>

        sum +
        Number(
          x.accepted_qty || 0
        ),

      0
    ),

    rejectedQty: rows.reduce(

      (sum, x) =>

        sum +
        Number(
          x.rejected_qty || 0
        ),

      0
    )
  };


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.page}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.headerCard}>


        <div>

          <div style={styles.headerTitle}>

            Reconciliation Workbench

          </div>

          <div style={styles.headerSubtitle}>

            Execute final reconciliation
            and inventory acceptance

          </div>

        </div>


        <div style={styles.headerActions}>


          <button
            style={styles.refreshBtn}
            onClick={loadReceipts}
          >

            <RefreshCw size={16} />

            Refresh

          </button>

        </div>

      </div>


      {/* ================================================================ */}
      {/* KPI SECTION */}
      {/* ================================================================ */}
      <div style={styles.kpiGrid}>


        <KPIBox
          title="Pending Reconciliation"
          value={kpis.submitted}
          color="#ea580c"
        />

        <KPIBox
          title="Reconciled"
          value={kpis.reconciled}
          color="#16a34a"
        />

        <KPIBox
          title="Accepted Qty"
          value={kpis.acceptedQty}
          color="#0891b2"
        />

        <KPIBox
          title="Rejected Qty"
          value={kpis.rejectedQty}
          color="#dc2626"
        />

      </div>


      {/* ================================================================ */}
      {/* FILTER PANEL */}
      {/* ================================================================ */}
      <div style={styles.filterCard}>


        <div style={styles.filterGrid}>


          {/* ------------------------------------------------------------ */}
          {/* STATUS */}
          {/* ------------------------------------------------------------ */}
          <div>

            <div style={styles.label}>
              Reconciliation Status
            </div>

            <select

              style={styles.select}

              value={filters.status}

              onChange={(e) =>

                handleFilterChange(

                  "status",

                  e.target.value
                )
              }
            >

              <option value="">
                ALL
              </option>

              <option value="SUBMITTED">
                SUBMITTED
              </option>

              <option value="RECONCILED">
                RECONCILED
              </option>

            </select>

          </div>


          {/* ------------------------------------------------------------ */}
          {/* SEARCH */}
          {/* ------------------------------------------------------------ */}
          <div>

            <div style={styles.label}>
              Search Receipt
            </div>

            <input

              type="text"

              style={styles.input}

              placeholder="
                Search receipt/project/store
              "

              value={filters.search}

              onChange={(e) =>

                handleFilterChange(

                  "search",

                  e.target.value
                )
              }
            />

          </div>


          {/* ------------------------------------------------------------ */}
          {/* ACTION */}
          {/* ------------------------------------------------------------ */}
          <div style={styles.filterAction}>

            <button
              style={styles.applyBtn}
              onClick={
                handleApplyFilters
              }
            >

              Apply Filters

            </button>

          </div>

        </div>

      </div>


      {/* ================================================================ */}
      {/* ERROR */}
      {/* ================================================================ */}
      {

        error && (

          <div style={styles.errorBox}>

            {error}

          </div>
        )
      }


      {/* ================================================================ */}
      {/* GRID */}
      {/* ================================================================ */}
      <div style={styles.gridCard}>


        {/* -------------------------------------------------------------- */}
        {/* TABLE HEADER */}
        {/* -------------------------------------------------------------- */}
        <div style={styles.tableHeader}>


          <div style={styles.colReceipt}>
            Receipt
          </div>

          <div style={styles.colProject}>
            Project
          </div>

          <div style={styles.colStore}>
            Site Store
          </div>

          <div style={styles.colAccepted}>
            Accepted
          </div>

          <div style={styles.colRejected}>
            Rejected
          </div>

          <div style={styles.colStatus}>
            Status
          </div>

          <div style={styles.colAction}>
            Actions
          </div>

        </div>


        {/* -------------------------------------------------------------- */}
        {/* LOADING */}
        {/* -------------------------------------------------------------- */}
        {

          loading && (

            <div style={styles.placeholder}>

              Loading reconciliation records...

            </div>
          )
        }


        {/* -------------------------------------------------------------- */}
        {/* EMPTY */}
        {/* -------------------------------------------------------------- */}
        {

          !loading &&
          rows.length === 0 && (

            <div style={styles.placeholder}>

              No reconciliation records found

            </div>
          )
        }


        {/* -------------------------------------------------------------- */}
        {/* ROWS */}
        {/* -------------------------------------------------------------- */}
        {

          rows.map((row) => (

            <div
              key={row.id}
              style={styles.tableRow}
            >


              <div style={styles.colReceipt}>


                <div style={styles.mainText}>

                  {row.receipt_no}

                </div>


                <div style={styles.subText}>

                  Receipt:
                  {" "}

                  {row.receipt_date}

                </div>

              </div>


              <div style={styles.colProject}>

                {row.project_code || "-"}

              </div>


              <div style={styles.colStore}>

                {row.site_store_name || "-"}

              </div>


              <div style={styles.colAccepted}>

                {row.accepted_qty || 0}

              </div>


              <div style={styles.colRejected}>

                {row.rejected_qty || 0}

              </div>


              <div style={styles.colStatus}>


                <span style={
                  getStatusStyle(
                    row.receipt_status
                  )
                }>

                  {row.receipt_status}

                </span>

              </div>


              <div style={styles.colAction}>


                <button

                  style={styles.viewBtn}

                  onClick={() =>
                    openReconciliation(row.id)
                  }
                >

                  👁

                </button>

              </div>

            </div>
          ))
        }

      </div>

    </div>
  );
}


// =============================================================================
// KPI BOX
// =============================================================================

function KPIBox({

  title,

  value,

  color

}) {

  return (

    <div
      style={{

        ...styles.kpiCard,

        borderTop:
          `4px solid ${color}`
      }}
    >

      <div style={styles.kpiTitle}>
        {title}
      </div>

      <div style={styles.kpiValue}>
        {value}
      </div>

    </div>
  );
}


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "RECONCILED":

      return styles.statusGreen;

    case "SUBMITTED":

      return styles.statusOrange;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  page: {

    padding: 20,

    background: "#f5f7fb",

    minHeight: "100vh"
  },

  headerCard: {

    background:
      "linear-gradient(90deg, #059669, #10b981)",

    borderRadius: 14,

    padding: "18px 22px",

    marginBottom: 20,

    color: "#fff",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  headerTitle: {

    fontSize: 24,

    fontWeight: 700
  },

  headerSubtitle: {

    fontSize: 13,

    opacity: 0.92,

    marginTop: 4
  },

  headerActions: {

    display: "flex",

    gap: 10
  },

  refreshBtn: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    background: "#fff",

    color: "#059669",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    fontWeight: 600,

    cursor: "pointer"
  },

  kpiGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",

    gap: 16,

    marginBottom: 20
  },

  kpiCard: {

    background: "#fff",

    borderRadius: 12,

    padding: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  kpiTitle: {

    fontSize: 13,

    color: "#6b7280",

    marginBottom: 10
  },

  kpiValue: {

    fontSize: 34,

    fontWeight: 700,

    color: "#111827"
  },

  filterCard: {

    background: "#fff",

    borderRadius: 12,

    padding: 20,

    marginBottom: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  filterGrid: {

    display: "grid",

    gridTemplateColumns:
      "1fr 2fr auto",

    gap: 16,

    alignItems: "end"
  },

  label: {

    fontSize: 13,

    color: "#374151",

    marginBottom: 6,

    fontWeight: 600
  },

  select: {

    width: "100%",

    border: "1px solid #d1d5db",

    borderRadius: 8,

    padding: "10px 12px"
  },

  input: {

    width: "100%",

    border: "1px solid #d1d5db",

    borderRadius: 8,

    padding: "10px 12px"
  },

  filterAction: {

    display: "flex",

    alignItems: "end"
  },

  applyBtn: {

    background: "#059669",

    color: "#fff",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    fontWeight: 600,

    cursor: "pointer"
  },

  errorBox: {

    background: "#fee2e2",

    color: "#b91c1c",

    padding: 14,

    borderRadius: 10,

    marginBottom: 20
  },

  gridCard: {

    background: "#fff",

    borderRadius: 12,

    padding: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  tableHeader: {

    display: "grid",

    gridTemplateColumns:
      "1.8fr 1.5fr 1.5fr 1fr 1fr 1fr 0.7fr",

    gap: 16,

    padding: "14px 16px",

    background: "#f9fafb",

    border:
      "1px solid #e5e7eb",

    borderRadius: 10,

    fontSize: 13,

    fontWeight: 700,

    color: "#374151",

    marginBottom: 10
  },

  tableRow: {

    display: "grid",

    gridTemplateColumns:
      "1.8fr 1.5fr 1.5fr 1fr 1fr 1fr 0.7fr",

    gap: 16,

    padding: "14px 16px",

    border:
      "1px solid #e5e7eb",

    borderRadius: 10,

    alignItems: "center",

    background: "#fff",

    marginBottom: 10
  },

  colReceipt: {

    display: "flex",

    flexDirection: "column"
  },

  colProject: {

    display: "flex",

    alignItems: "center"
  },

  colStore: {

    display: "flex",

    alignItems: "center"
  },

  colAccepted: {

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    fontWeight: 600
  },

  colRejected: {

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    fontWeight: 600
  },

  colStatus: {

    display: "flex",

    alignItems: "center"
  },

  colAction: {

    display: "flex",

    justifyContent: "center"
  },

  mainText: {

    fontWeight: 600,

    color: "#111827"
  },

  subText: {

    fontSize: 12,

    color: "#6b7280",

    marginTop: 4
  },

  placeholder: {

    padding: 40,

    textAlign: "center",

    color: "#6b7280"
  },

  viewBtn: {

    border:
      "1px solid #d1d5db",

    background: "#fff",

    borderRadius: 8,

    padding: "8px 12px",

    cursor: "pointer"
  },

  statusGray: {

    background: "#f3f4f6",

    color: "#374151",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },

  statusOrange: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#15803d",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  }
};